import React, { useEffect, useState, useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { FileTextIcon, CheckCircleIcon, XCircleIcon, ClockIcon, PercentIcon, GlobeIcon, BuildingIcon, UsersIcon, CalendarIcon, ChevronDownIcon, PencilIcon } from 'lucide-react';
import { BIKPI } from './BIKPI';
import { BICard } from './BICard';
import { BIInsights } from './BIInsights';
import { globalKPIs, buComparisonData, trendData, countryData, dealerData, globalInsights } from '../../data/biData';
const COLORS = ['#3b82f6', '#10b981', '#ef4444', '#f59e0b', '#8b5cf6'];
type DateFilterMode = 'year' | 'quarter' | 'month';
const currentYear = new Date().getFullYear();
const years = Array.from({
  length: 6
}, (_, i) => currentYear - i);
const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export function GlobalView() {
  const [dateMode, setDateMode] = useState<DateFilterMode>('year');
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedQuarter, setSelectedQuarter] = useState('Q1');
  const [selectedMonth, setSelectedMonth] = useState('January');
  const [isEditingDate, setIsEditingDate] = useState(false);
  const datePickerRef = useRef<HTMLDivElement>(null);
  const dateLabel = dateMode === 'year' ? `${selectedYear}` : dateMode === 'quarter' ? `${selectedQuarter} ${selectedYear}` : `${selectedMonth} ${selectedYear}`;
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        setIsEditingDate(false);
      }
    }
    if (isEditingDate) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isEditingDate]);
  return <div className="space-y-6" data-id="element-825">
      {/* Global Identity Header — globe icon + network stats */}
      <div className="flex flex-wrap items-center gap-4" data-id="element-826">
        {/* Globe Identity */}
        <div className="flex items-center gap-4 mr-auto" data-id="element-827">
          <div className="w-12 h-12 flex items-center justify-center flex-shrink-0" data-id="element-828">
            <img src="https://cdn.magicpatterns.com/uploads/jJzF1PFVpDXeTTbKYPBGHa/earth-globe-with-continents-maps.svg" alt="Global Network" className="w-12 h-12 filter invert opacity-90" data-id="element-829" />
          </div>
          <div data-id="element-830">
            <h3 className="text-white font-semibold text-[22px]" data-id="element-831">
              Global Quotes Analytics
            </h3>
            <p className="text-blue-300/70 text-sm leading-tight mt-0.5" data-id="element-832">
              Global analysis of quotes across all business units
            </p>
            {/* Inline date filter */}
            <div className="relative mt-1" ref={datePickerRef} data-id="element-833">
              <button onClick={() => setIsEditingDate(!isEditingDate)} className="flex items-center gap-2 text-sm text-blue-300 hover:text-white transition-colors group" data-id="element-834">
                <CalendarIcon size={15} className="text-blue-400/70 group-hover:text-blue-300" data-id="element-835" />
                <span className="font-medium" data-id="element-836">{dateLabel}</span>
                <PencilIcon size={12} className="opacity-0 group-hover:opacity-100 transition-opacity text-blue-400" data-id="element-837" />
              </button>

              {/* Date picker popover */}
              {isEditingDate && <div className="absolute top-full left-0 mt-2 bg-[#0f1d3a] rounded-xl border border-white/10 shadow-2xl p-3 z-50 min-w-[240px]" data-id="element-838">
                  <div className="space-y-2.5" data-id="element-839">
                    {/* Period type */}
                    <div data-id="element-840">
                      <p className="text-[10px] uppercase tracking-wider text-blue-400/60 mb-1" data-id="element-841">
                        Period
                      </p>
                      <div className="flex gap-1" data-id="element-842">
                        {(['year', 'quarter', 'month'] as DateFilterMode[]).map(mode => <button key={mode} onClick={() => setDateMode(mode)} className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${dateMode === mode ? 'bg-blue-600 text-white' : 'bg-white/5 text-blue-300 hover:bg-white/10 hover:text-white'}`} data-id="element-843">
                              {mode.charAt(0).toUpperCase() + mode.slice(1)}
                            </button>)}
                      </div>
                    </div>

                    {/* Month selector */}
                    {dateMode === 'month' && <div data-id="element-844">
                        <p className="text-[10px] uppercase tracking-wider text-blue-400/60 mb-1" data-id="element-845">
                          Month
                        </p>
                        <div className="relative" data-id="element-846">
                          <select value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/10 rounded-lg px-3 py-1.5 pr-7 text-xs font-medium text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all cursor-pointer" data-id="element-847">
                            {months.map(m => <option key={m} value={m} className="bg-slate-800 text-white" data-id="element-848">
                                {m}
                              </option>)}
                          </select>
                          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1.5 text-blue-300" data-id="element-849">
                            <ChevronDownIcon size={12} data-id="element-850" />
                          </div>
                        </div>
                      </div>}

                    {/* Quarter selector */}
                    {dateMode === 'quarter' && <div data-id="element-851">
                        <p className="text-[10px] uppercase tracking-wider text-blue-400/60 mb-1" data-id="element-852">
                          Quarter
                        </p>
                        <div className="flex gap-1" data-id="element-853">
                          {quarters.map(q => <button key={q} onClick={() => setSelectedQuarter(q)} className={`flex-1 px-2 py-1 rounded-md text-xs font-medium transition-colors ${selectedQuarter === q ? 'bg-blue-600 text-white' : 'bg-white/5 text-blue-300 hover:bg-white/10 hover:text-white'}`} data-id="element-854">
                              {q}
                            </button>)}
                        </div>
                      </div>}

                    {/* Year selector */}
                    <div data-id="element-855">
                      <p className="text-[10px] uppercase tracking-wider text-blue-400/60 mb-1" data-id="element-856">
                        Year
                      </p>
                      <div className="relative" data-id="element-857">
                        <select value={selectedYear} onChange={e => setSelectedYear(Number(e.target.value))} className="w-full appearance-none bg-white/10 border border-white/10 rounded-lg px-3 py-1.5 pr-7 text-xs font-medium text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all cursor-pointer" data-id="element-858">
                          {years.map(y => <option key={y} value={y} className="bg-slate-800 text-white" data-id="element-859">
                              {y}
                            </option>)}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1.5 text-blue-300" data-id="element-860">
                          <ChevronDownIcon size={12} data-id="element-861" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>}
            </div>
          </div>
        </div>

        {/* Active BUs Panel */}
        <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl border border-white/10 px-5 py-3" data-id="element-862">
          <div className="flex items-center gap-3" data-id="element-863">
            <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center" data-id="element-864">
              <GlobeIcon size={18} className="text-blue-200/70" data-id="element-865" />
            </div>
            <div data-id="element-866">
              <p className="text-xs text-blue-300/70 leading-tight" data-id="element-867">
                Business Unit
              </p>
              <p className="text-xl font-bold text-white leading-tight" data-id="element-868">
                {globalKPIs.activeBUs}
              </p>
            </div>
          </div>
        </div>

        {/* Active Countries Panel */}
        <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl border border-white/10 px-5 py-3" data-id="element-869">
          <div className="flex items-center gap-3" data-id="element-870">
            <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center" data-id="element-871">
              <GlobeIcon size={18} className="text-blue-200/70" data-id="element-872" />
            </div>
            <div data-id="element-873">
              <p className="text-xs text-blue-300/70 leading-tight" data-id="element-874">
                Active Countries
              </p>
              <p className="text-xl font-bold text-white leading-tight" data-id="element-875">
                {globalKPIs.activeCountries}
              </p>
            </div>
          </div>
        </div>

        {/* Active Dealers Panel */}
        <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl border border-white/10 px-5 py-3" data-id="element-876">
          <div className="flex items-center gap-3" data-id="element-877">
            <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center" data-id="element-878">
              <BuildingIcon size={18} className="text-blue-200/70" data-id="element-879" />
            </div>
            <div data-id="element-880">
              <p className="text-xs text-blue-300/70 leading-tight" data-id="element-881">
                Active Dealers
              </p>
              <p className="text-xl font-bold text-white leading-tight" data-id="element-882">
                {globalKPIs.activeDealers}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* KPIs Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-883">
        <BIKPI title="Total Quotes" value={globalKPIs.totalQuotes} trend={globalKPIs.variation.total} icon={<FileTextIcon size={18} data-id="element-885" />} color="blue" delay={0.1} data-id="element-884" />
        <BIKPI title="Won Quotes" value={globalKPIs.wonQuotes} trend={globalKPIs.variation.won} icon={<CheckCircleIcon size={18} data-id="element-887" />} color="emerald" delay={0.2} data-id="element-886" />
        <BIKPI title="Win Rate" value={`${globalKPIs.winRate}%`} trend={globalKPIs.variation.winRate} icon={<PercentIcon size={18} data-id="element-889" />} color="teal" delay={0.3} data-id="element-888" />
        <BIKPI title="Active Quotes" value={globalKPIs.activeQuotes} icon={<ClockIcon size={18} data-id="element-891" />} color="amber" delay={0.4} data-id="element-890" />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-892">
        <BICard title="Quotes Trend (YTD)" className="lg:col-span-2" data-id="element-893">
          <div className="h-80" data-id="element-894">
            <ResponsiveContainer width="100%" height="100%" data-id="element-895">
              <LineChart data={trendData} margin={{
              top: 20,
              right: 30,
              left: 0,
              bottom: 5
            }} data-id="element-896">
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" vertical={false} data-id="element-897" />
                <XAxis dataKey="month" stroke="#93c5fd" fontSize={12} tickLine={false} axisLine={false} data-id="element-898" />
                <YAxis stroke="#93c5fd" fontSize={12} tickLine={false} axisLine={false} data-id="element-899" />
                <Tooltip contentStyle={{
                backgroundColor: '#0f1d3a',
                borderColor: '#ffffff20',
                borderRadius: '8px',
                color: '#fff'
              }} data-id="element-900" />
                <Legend wrapperStyle={{
                fontSize: '12px',
                color: '#93c5fd'
              }} data-id="element-901" />
                <Line type="monotone" dataKey="total" name="Total Quotes" stroke="#3b82f6" strokeWidth={3} dot={{
                r: 4,
                fill: '#3b82f6',
                strokeWidth: 2,
                stroke: '#0f1d3a'
              }} activeDot={{
                r: 6
              }} data-id="element-902" />
                <Line type="monotone" dataKey="won" name="Won Quotes" stroke="#10b981" strokeWidth={3} dot={{
                r: 4,
                fill: '#10b981',
                strokeWidth: 2,
                stroke: '#0f1d3a'
              }} data-id="element-903" />
                <Line type="monotone" dataKey="lost" name="Lost Quotes" stroke="#ef4444" strokeWidth={3} dot={{
                r: 4,
                fill: '#ef4444',
                strokeWidth: 2,
                stroke: '#0f1d3a'
              }} data-id="element-904" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </BICard>

        <BICard title="Quotes by Business Unit" data-id="element-905">
          <div className="h-72" data-id="element-906">
            <ResponsiveContainer width="100%" height="100%" data-id="element-907">
              <BarChart data={buComparisonData} margin={{
              top: 20,
              right: 30,
              left: 0,
              bottom: 5
            }} data-id="element-908">
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" vertical={false} data-id="element-909" />
                <XAxis dataKey="name" stroke="#93c5fd" fontSize={12} tickLine={false} axisLine={false} data-id="element-910" />
                <YAxis stroke="#93c5fd" fontSize={12} tickLine={false} axisLine={false} data-id="element-911" />
                <Tooltip contentStyle={{
                backgroundColor: '#0f1d3a',
                borderColor: '#ffffff20',
                borderRadius: '8px',
                color: '#fff'
              }} itemStyle={{
                color: '#fff'
              }} data-id="element-912" />
                <Legend wrapperStyle={{
                fontSize: '12px',
                color: '#93c5fd'
              }} data-id="element-913" />
                <Bar dataKey="won" name="Won" stackId="a" fill="#10b981" radius={[0, 0, 4, 4]} data-id="element-914" />
                <Bar dataKey="active" name="Active" stackId="a" fill="#3b82f6" data-id="element-915" />
                <Bar dataKey="lost" name="Lost" stackId="a" fill="#ef4444" radius={[4, 4, 0, 0]} data-id="element-916" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </BICard>

        <BICard title="Win Rate by Business Unit" data-id="element-917">
          <div className="h-72" data-id="element-918">
            <ResponsiveContainer width="100%" height="100%" data-id="element-919">
              <BarChart data={buComparisonData} layout="vertical" margin={{
              top: 20,
              right: 30,
              left: 40,
              bottom: 5
            }} data-id="element-920">
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" horizontal={false} data-id="element-921" />
                <XAxis type="number" domain={[0, 100]} stroke="#93c5fd" fontSize={12} tickLine={false} axisLine={false} data-id="element-922" />
                <YAxis dataKey="name" type="category" stroke="#93c5fd" fontSize={12} tickLine={false} axisLine={false} data-id="element-923" />
                <Tooltip contentStyle={{
                backgroundColor: '#0f1d3a',
                borderColor: '#ffffff20',
                borderRadius: '8px',
                color: '#fff'
              }} formatter={value => [`${value}%`, 'Win Rate']} data-id="element-924" />
                <Bar dataKey="winRate" name="Win Rate" fill="#0ea5e9" radius={[0, 4, 4, 0]} barSize={20} data-id="element-925">
                  {buComparisonData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.winRate > 75 ? '#10b981' : entry.winRate < 70 ? '#ef4444' : '#0ea5e9'} data-id="element-926" />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </BICard>
      </div>

      {/* Tables & Insights Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-927">
        <div className="lg:col-span-2 space-y-6" data-id="element-928">
          <BICard title="Business Units Performance" data-id="element-929">
            <div className="overflow-x-auto" data-id="element-930">
              <table className="w-full text-sm text-left" data-id="element-931">
                <thead className="text-xs text-blue-300 uppercase bg-white/5 border-b border-white/10" data-id="element-932">
                  <tr data-id="element-933">
                    <th className="px-4 py-3 font-medium" data-id="element-934">Business Unit</th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-935">Total</th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-936">Won</th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-937">Active</th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-938">
                      Win Rate
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-939">
                  {buComparisonData.map((bu, idx) => <tr key={idx} className="border-b border-white/5 hover:bg-white/5 transition-colors" data-id="element-940">
                      <td className="px-4 py-3 font-medium text-white" data-id="element-941">
                        {bu.name}
                      </td>
                      <td className="px-4 py-3 text-right text-blue-100" data-id="element-942">
                        {bu.total}
                      </td>
                      <td className="px-4 py-3 text-right text-emerald-400" data-id="element-943">
                        {bu.won}
                      </td>
                      <td className="px-4 py-3 text-right text-blue-400" data-id="element-944">
                        {bu.active}
                      </td>
                      <td className="px-4 py-3 text-right text-white" data-id="element-945">
                        <span className={`px-2 py-1 rounded-full text-xs ${bu.winRate > 75 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-blue-500/20 text-blue-400'}`} data-id="element-946">
                          {bu.winRate}%
                        </span>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </BICard>

          <BICard title="Top Countries" data-id="element-947">
            <div className="overflow-x-auto" data-id="element-948">
              <table className="w-full text-sm text-left" data-id="element-949">
                <thead className="text-xs text-blue-300 uppercase bg-white/5 border-b border-white/10" data-id="element-950">
                  <tr data-id="element-951">
                    <th className="px-4 py-3 font-medium" data-id="element-952">Country</th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-953">Total</th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-954">Won</th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-955">
                      Win Rate
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-956">
                  {countryData.slice(0, 5).map((country, idx) => <tr key={idx} className="border-b border-white/5 hover:bg-white/5 transition-colors" data-id="element-957">
                      <td className="px-4 py-3 font-medium text-white" data-id="element-958">
                        {country.name}
                      </td>
                      <td className="px-4 py-3 text-right text-blue-100" data-id="element-959">
                        {country.total}
                      </td>
                      <td className="px-4 py-3 text-right text-emerald-400" data-id="element-960">
                        {country.won}
                      </td>
                      <td className="px-4 py-3 text-right text-white" data-id="element-961">
                        {country.winRate}%
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </BICard>
        </div>

        <div className="lg:col-span-1" data-id="element-962">
          <BIInsights insights={globalInsights} data-id="element-963" />
        </div>
      </div>
    </div>;
}